import React, { useState, useRef, useEffect, useCallback } from "react";
import { IconButton } from "@wealthx/ui/icon-button";
import { Menu } from "@wealthx/ui/menu";
import { MenuItem } from "@wealthx/ui/menu-item";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { ConversationItem } from "../../../api-services/conversation";

interface ConversationListItemProps {
  conversation: ConversationItem;
  isActive: boolean;
  onSelect: (id: string) => void;
  onDelete: (id: string) => void;
  onRename: (id: string, title: string) => void;
}

const ConversationListItem: React.FC<ConversationListItemProps> = ({
  conversation,
  isActive,
  onSelect,
  onDelete,
  onRename,
}) => {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [isRenaming, setIsRenaming] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const menuOpenRef = useRef(false);

  const displayTitle = conversation.title || "Untitled chat";
  const isUntitled = !conversation.title;

  useEffect(() => {
    if (isRenaming && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isRenaming]);

  // Track menu open state via ref to prevent click-through on close
  useEffect(() => {
    menuOpenRef.current = Boolean(menuAnchor);
  }, [menuAnchor]);

  const handleMenuOpen = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      setMenuAnchor(e.currentTarget);
    },
    []
  );

  const handleMenuClose = useCallback(() => {
    setMenuAnchor(null);
    // Keep menuOpenRef true briefly so handleClick ignores the close click
    setTimeout(() => {
      menuOpenRef.current = false;
    }, 0);
  }, []);

  const handleStartRename = useCallback(() => {
    setRenameValue(conversation.title || "");
    setIsRenaming(true);
    handleMenuClose();
  }, [conversation.title, handleMenuClose]);

  const handleConfirmRename = useCallback(() => {
    const trimmed = renameValue.trim();
    if (trimmed && trimmed !== conversation.title) {
      onRename(conversation.conversationID, trimmed);
    }
    setIsRenaming(false);
  }, [renameValue, conversation.title, conversation.conversationID, onRename]);

  const handleRenameKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        handleConfirmRename();
      } else if (e.key === "Escape") {
        setIsRenaming(false);
      }
    },
    [handleConfirmRename]
  );

  const handleStartDelete = useCallback(() => {
    setIsDeleting(true);
    handleMenuClose();
  }, [handleMenuClose]);

  const handleConfirmDelete = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onDelete(conversation.conversationID);
      setIsDeleting(false);
    },
    [conversation.conversationID, onDelete]
  );

  const handleCancelDelete = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsDeleting(false);
  }, []);

  const handleClick = useCallback(() => {
    // Skip if menu was just open (closing menu triggers click on parent)
    if (menuOpenRef.current || isRenaming || isDeleting) {
      return;
    }
    onSelect(conversation.conversationID);
  }, [isRenaming, isDeleting, onSelect, conversation.conversationID]);

  if (isDeleting) {
    return (
      <div className="conversation-list__item conversation-list__delete-confirm">
        <span className="conversation-list__delete-confirm-text">
          Delete this chat?
        </span>
        <div className="conversation-list__delete-confirm-actions">
          <button
            type="button"
            className="conversation-list__delete-confirm-btn conversation-list__delete-confirm-btn--yes"
            onClick={handleConfirmDelete}
          >
            Yes
          </button>
          <button
            type="button"
            className="conversation-list__delete-confirm-btn conversation-list__delete-confirm-btn--no"
            onClick={handleCancelDelete}
          >
            No
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`conversation-list__item${isActive ? " conversation-list__item--active" : ""}`}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " " || event.key === "Spacebar") {
          event.preventDefault();
          handleClick();
        }
      }}
    >
      {isRenaming ? (
        <input
          ref={inputRef}
          className="conversation-list__rename-input"
          value={renameValue}
          onChange={(e) => setRenameValue(e.target.value)}
          onKeyDown={handleRenameKeyDown}
          onBlur={handleConfirmRename}
          maxLength={100}
          onClick={(e) => e.stopPropagation()}
        />
      ) : (
        <span
          className={`conversation-list__item-title${isUntitled ? " conversation-list__item-title--untitled" : ""}`}
        >
          {displayTitle}
        </span>
      )}

      {!isRenaming && (
        <IconButton
          className="conversation-list__item-actions"
          size="small"
          onClick={handleMenuOpen}
          sx={{ color: "rgba(255, 255, 255, 0.7)", padding: "2px" }}
        >
          <MoreHorizIcon sx={{ fontSize: 16 }} />
        </IconButton>
      )}

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              minWidth: 120,
              bgcolor: "#162029",
              color: "white",
              border: "1px solid rgba(255, 255, 255, 0.12)",
              "& .MuiMenuItem-root:hover": {
                bgcolor: "rgba(255, 255, 255, 0.1)",
              },
            },
          },
        }}
      >
        <MenuItem onClick={handleStartRename}>Rename</MenuItem>
        <MenuItem onClick={handleStartDelete} sx={{ color: "#f44336" }}>
          Delete
        </MenuItem>
      </Menu>
    </div>
  );
};

export default ConversationListItem;
